import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import logo from '../../assets/FinalLOGO.png';
import {
  Users,
  Target,
  TrendingUp,
  Zap,
  ArrowRight,
  ClipboardList,
  CheckCircle2,
  Clock,
  Plus,
  Rocket
} from 'lucide-react';
import { Link } from 'react-router-dom';

const MarketingAgentDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalLeads: 0,
    convertedLeads: 0,
    pendingPayments: 0,
    totalEarnings: 0
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMarketingData();
  }, []);

  const fetchMarketingData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard');

      const dashboardData = response.data.data;

      setStats({
        totalLeads: dashboardData.stats?.totalLeads || 0,
        convertedLeads: dashboardData.stats?.convertedLeads || 0,
        pendingPayments: dashboardData.stats?.pendingPayments || 0,
        totalEarnings: dashboardData.stats?.totalEarnings || 0
      });

      setRecentLeads(dashboardData.recentLeads || []);
    } catch (error) {
      console.error('Marketing Dashboard Load Error:', error);
      toast.error('Failed to load dashboard data');
      // Fallback
      setStats({
        totalLeads: 0,
        convertedLeads: 0,
        pendingPayments: 0,
        totalEarnings: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-[2rem] p-8 shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col justify-between">
      <div className="flex justify-between items-start">
        <div className={`p-4 rounded-2xl ${color} text-white shadow-lg`}>
          {icon}
        </div>
      </div>
      <div className="mt-6">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</p>
        <p className="text-3xl font-black text-slate-800 mt-1">
          {typeof value === 'number' && title.includes('Earnings')
            ? (value === 0 ? '₹0' : (value >= 1000 ? `₹${(value / 1000).toFixed(1).replace('.0', '')}k` : `₹${value}`))
            : value}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 space-y-10 animate-pulse bg-slate-50 min-h-screen">
        <div className="flex items-center gap-6 mb-10">
          <img src={logo} alt="Loading..." className="h-20 w-auto opacity-50 grayscale" />
          <div className="h-20 flex-1 bg-white rounded-3xl" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-44 bg-white rounded-[2rem]" />)}
        </div>
        <div className="h-96 bg-white rounded-[3rem]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-10 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-5xl font-black text-slate-900 tracking-tighter">Lead Partner : {user?.firstName}</h1>
          <p className="text-slate-500 font-medium mt-1">Track your referral pipeline, conversions, and payouts at a glance.</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <StatCard
          title="Total Leads"
          value={stats.totalLeads}
          icon={<Users className="w-6 h-6" />}
          color="bg-indigo-600"
        />
        <StatCard
          title="Conversions"
          value={stats.convertedLeads}
          icon={<Target className="w-6 h-6" />}
          color="bg-blue-600"
        />
        <StatCard
          title="Pending Payouts"
          value={stats.pendingPayments}
          icon={<Clock className="w-6 h-6" />}
          color="bg-orange-500"
        />
        <StatCard
          title="Total Earnings"
          value={stats.totalEarnings}
          icon={<TrendingUp className="w-6 h-6" />}
          color="bg-emerald-500"
        />
      </div>

      <div className="grid grid-cols-1 gap-10">
        {/* Recent Activity */}
        <div className="bg-white rounded-[3rem] shadow-2xl shadow-slate-200/30 border border-slate-100 p-10">
          <div className="flex items-center justify-between mb-10 pb-6 border-b border-slate-50">
            <div>
              <h2 className="text-2xl font-black text-slate-800">Recent Sourcing Activity</h2>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Latest patient referrals & status</p>
            </div>
            <Link to="/leads" className="text-xs font-black text-indigo-600 uppercase tracking-widest">My Pipeline</Link>
          </div>

          <div className="space-y-4">
            {recentLeads.map((lead, i) => (
              <div key={i} className="flex items-center justify-between p-6 bg-slate-50/50 border border-slate-100 rounded-[2.5rem] hover:bg-white hover:shadow-xl transition-all group">
                <div className="flex items-center gap-6">
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-slate-300 group-hover:text-indigo-600 shadow-sm transition-colors">
                    <ClipboardList className="w-7 h-7" />
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-slate-800 tracking-tight">{lead.patientName}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded text-[9px] font-black uppercase tracking-widest">{lead.status}</span>
                      <span className="text-[10px] text-slate-300 font-bold">Created {new Date(lead.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {lead.status === 'completed' && <CheckCircle2 className="w-6 h-6 text-emerald-500" />}
                  <Link to={`/leads/${lead._id}`} className="w-10 h-10 bg-white rounded-xl shadow-sm border border-slate-50 flex items-center justify-center text-slate-300 hover:text-indigo-600 hover:scale-110 transition-all">
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
            {recentLeads.length === 0 && <p className="text-center py-20 text-slate-300 font-bold italic">No active leads found in your profile.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingAgentDashboard;